
import React, { memo } from "react";
import NodeHeader from "./flowNode/NodeHeader";
import NodeContent from "./flowNode/NodeContent";
import ResizeHandle from "./flowNode/ResizeHandle";
import NodeContainer from "./flowNode/NodeContainer";
import { validateFlowNodeProps, prepareFlowNodeState } from "./flowNode/FlowNodeValidation";
import { useFlowNodeHooks } from "./flowNode/FlowNodeHooks";
import { createNodeClickHandler } from "./flowNode/FlowNodeClickHandler";

import type { FlowNodeProps } from "./flowNode/FlowNodeTypes";

const FlowNode = ({ id, data, selected, dragging }: FlowNodeProps) => {
  

  // Validate props with error handling
  try {
    const validation = validateFlowNodeProps(id, data);
    if (!validation.isValid) {
      console.error("FlowNode validation failed:", validation.error);
      return <div className="error-node bg-red-100 p-2 rounded text-red-600">{validation.error}</div>;
    }

    // Prepare node state
    const state = prepareFlowNodeState(data);
    
    // Initialize all hooks with error handling
    const {
      isResizing,
      handleResizeStart,
      isEditingContent,
      handleContentClick,
      handleContentChange,
      handleContentBlur,
      handleCategoryChange
    } = useFlowNodeHooks(id, data, state);
    
    // Create click handler
    const handleNodeClick = createNodeClickHandler(id, data);
    
    return (
      <div 
        onClick={handleNodeClick} 
        style={{ 
          position: 'relative', 
          zIndex: 1,
          pointerEvents: dragging ? 'none' : 'auto'
        }}
        key={`${id}-${state.currentShape}`}
      >
        <NodeContainer
          id={id}
          selected={Boolean(selected)}
          nodeWidth={state.nodeWidth}
          nodeHeight={state.nodeHeight}
          shape={state.currentShape}
          isResizing={isResizing}
          wasRecentlyEdited={state.wasRecentlyEdited}
          editedBy={data.editedBy}
          isClientMode={Boolean(data.isClientMode)}
          isDragging={Boolean(dragging)}
        >
          <NodeHeader
            category={data.category || "Message"}
            color="linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--muted) / 0.8) 100%)"
            onCategoryChange={handleCategoryChange}
            isClientMode={Boolean(data.isClientMode)}
          />
          
          <NodeContent
            label={data.label || ""}
            nodeHeight={state.nodeHeight}
            nodeWidth={state.nodeWidth}
            shape={state.currentShape}
            isSelected={Boolean(selected)}
            isEditingContent={isEditingContent}
            isResizing={isResizing}
            onContentClick={handleContentClick}
            onContentChange={handleContentChange}
            onContentBlur={handleContentBlur}
            clientEdits={data.clientEdits}
            isClientMode={Boolean(data.isClientMode)}
          />

          <ResizeHandle 
            selected={Boolean(selected)} 
            onResizeStart={handleResizeStart} 
          />
        </NodeContainer>
      </div>
    );
  } catch (error) {
    console.error("Critical error in FlowNode:", error);
    return (
      <div className="error-node bg-red-100 p-4 rounded text-red-600">
        <div className="font-bold">FlowNode Error</div>
        <div className="text-sm">Node ID: {id}</div>
        <div className="text-xs">{error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }
};

// Enhanced memo comparison with error handling
export default memo(FlowNode, (prevProps, nextProps) => {
  try {
    return (
      prevProps.id === nextProps.id &&
      prevProps.selected === nextProps.selected &&
      prevProps.dragging === nextProps.dragging &&
      prevProps.data.label === nextProps.data.label &&
      prevProps.data.color === nextProps.data.color &&
      prevProps.data.category === nextProps.data.category &&
      prevProps.data.shape === nextProps.data.shape &&
      prevProps.data.width === nextProps.data.width &&
      prevProps.data.height === nextProps.data.height
    );
  } catch (error) {
    console.error("Error in FlowNode memo comparison:", error);
    return false;
  }
});
